import type { ClaimsProps } from '@/components/renters/Claims';
import type { HeroProps } from '@/components/renters/Hero';
import type { CustomersReviewsProps, ProductsProps } from '@/components/shared';
import { sharedConfig } from '@/pageConfig/shared';
import ogImage from '@/static/assets/renters/OG_FR_FR.png';
import bicyclesImg from '@/static/assets/renters/stuff/lg/bicycles.svg';
import cameraImg from '@/static/assets/renters/stuff/lg/camera.svg';
import type { BasePageConfig } from '../../BasePageConfig';
import { BottomCTAProps } from '@/components/renters/BottomCTA';
import type { CoverageProps } from '@/components/homeowners/coverage/Coverage';
import { GetCoveredProps } from '@/components/renters/GetCovered';
// images
import mobile5 from '@/static/assets/renters/coverage_fr/5-mobile.svg';
import desktop5 from '@/static/assets/renters/coverage_fr/5-desktop.svg';
import mobile4 from '@/static/assets/renters/coverage_fr/4-mobile.svg';
import desktop4 from '@/static/assets/renters/coverage_fr/4-desktop.svg';
import mobile3 from '@/static/assets/renters/coverage_fr/3-mobile.svg';
import desktop3 from '@/static/assets/renters/coverage_fr/3-desktop.svg';
import mobile2 from '@/static/assets/renters/coverage_fr/2-mobile.svg';
import desktop2 from '@/static/assets/renters/coverage_fr/2-desktop.svg';
import mobile1 from '@/static/assets/renters/coverage_fr/1-mobile.svg';
import desktop1 from '@/static/assets/renters/coverage_fr/1-desktop.svg';
import animationConfig from '@/static/assets/renters/RT_Bottom_Illustration.json';

import arrowIndicatorLeft from '@/static/assets/renters/claims/fr/fr-fr-left.svg';
import arrowIndicatorRight from '@/static/assets/renters/claims/fr/fr-fr-right.svg';
import claimsMobilePhone from '@/static/assets/renters/claims/fr/phone-fr-fr.svg';
import phoneTwoConfigFr from '@/static/assets/renters/phone_right_fr.json';

const productJsonld = {
  '@context': 'https://schema.org/',
  '@type': 'Product',
  name: 'Insurance policies for Renters',
  description: 'Insurance policies for Renters',
  image: [bicyclesImg.src, cameraImg.src],
  brand: {
    '@type': 'Brand',
    name: 'Lemonade',
    logo: 'https://www.lemonade.com/assets/logo-e0f7c4f109ef5af2bb9604137856587ca61a56ed8c3ab2b8f56fcec923babafc.svg',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    ratingCount: '62818',
  },
  offers: {
    '@type': 'AggregateOffer',
    offerCount: '',
    lowPrice: '5',
    highPrice: '',
    priceCurrency: 'USD',
  },
};

export interface Config extends BasePageConfig {
  hero: HeroProps;
  customersReviews: CustomersReviewsProps;
  coverage: GetCoveredProps;

  claims: ClaimsProps;

  frCoverage: CoverageProps;
  products: ProductsProps;

  bottomCTA: BottomCTAProps;
}

export const config: Config = {
  layout: {
    ...sharedConfig['fr-FR'].layout,
    alignLogoOnMobile: 'left',
    headerActions: {
      ...sharedConfig['fr-FR'].layout.headerActions,
      CTAs: {
        ...sharedConfig['fr-FR'].layout.headerActions.CTAs,
        checkOurPrices: {
          ...sharedConfig['fr-FR'].layout.headerActions.CTAs.checkOurPrices,
          customUrl: '/onboarding/start?f=1&ownership=renter',
          short: {
            singular: 'Consultez nos prix',
            plural: 'Consultez nos prix',
          },
        },
      },
    },
  },
  seo: {
    title: 'Assurance Habitation Locataire - Devis en Ligne | Lemonade',
    metaTitle: 'Assurance Habitation Locataire - Devis en Ligne | Lemonade',
    description:
      'Protégez votre logement et vos affaires avec Lemonade. Obtenez un devis en ligne en quelques minutes et découvrez une assurance adaptée à vos besoins.',
    ogImage: ogImage.src,
    jsonld: [productJsonld],
    uniqueCanonicalUrl: 'https://www.lemonade.com/fr/assurance-habitation-locataire',
  },
  hero: {
    title: 'Votre assurance habitation\npour locataires, pensée pour vous',
    subtitle: 'Rapide. Petits prix. Avec un grand cœur.',
    ctaLabel: 'CONSULTEZ NOS PRIX',
    trackingLabel: 'renters_hero',
    ctaUrl: '/onboarding/start?f=1&ownership=renter',
    stuffType: 'stuff_fr',
  },
  customersReviews: {
    title: "L'assurance habitation pour locataires\nla mieux notée en France",
    subtitle:
      'Lemonade est notée 4,9 étoiles sur l’App Store,\net classée parmi les meilleures assurances par <a href="https://fr.trustpilot.com/review/lemonade.com">Truspilot</a>',
    darkBackground: true,
  },
  coverage: {
    title: 'Assurez-vous en 90 secondes',
    subtitle: 'Protégez vos affaires instantanément grâce à notre IA.',
  },
  frCoverage: {
    darkBackground: true,
    pt: [45, 95],
    title: 'Ce qui est couvert par\nl’assurance habitation pour locataires',
    subtitle:
      'Elle protège vos effets personnels et vous aide à couvrir vos frais\nde responsabilité civile en cas de soucis.',
    data: [
      {
        title: "Si quelqu'un\nvole votre vélo",
        description:
          "La Garantie complète contre le vol couvre vos affaires même si elles sont volées à l'extérieur de votre domicile.",
        img: {
          src: {
            lg: desktop1,
            sm: mobile1,
          },
          alt: '',
        },
      },
      {
        title: "Si une fuite d'eau\nendommage vos affaires",
        description:
          'Nous couvrons vos biens si votre machine à laver provoque une fuite ou si vous êtes victime d’une rupture de canalisations.',
        img: {
          src: {
            lg: desktop2,
            sm: mobile2,
          },
          alt: '',
        },
      },
      {
        title: 'Si vos affaires\nprennent feu',
        description: 'Vos affaires sont couvertes contre des dommages causés par un incendie ou de la fumée.',
        img: {
          src: {
            lg: desktop3,
            sm: mobile3,
          },
          alt: '',
        },
      },
      {
        title: 'Si vous avez besoin\nde protection juridique',
        description:
          "Si vous avez besoin d'un avocat pour vous défendre, nous vous aidons à couvrir ces frais grâce à notre garantie Protection Juridique.",
        img: {
          src: {
            lg: desktop4,
            sm: mobile4,
          },
          alt: '',
        },
      },
      {
        title: "Si vous causez accidentellement des dommages à quelqu'un d'autre",
        description:
          'Vous êtes couvert(e) si vous causez accidentellement des dommages matériels ou corporels à un tiers.',
        img: {
          src: {
            lg: desktop5,
            sm: mobile5,
          },
          alt: '',
        },
      },
    ],
  },
  claims: {
    title: 'Traitement des sinistres\nen un temps record',
    subtitle: 'Sans paperasse. Sans tracas. Sans attente.',
    arrowIndicators: {
      left: arrowIndicatorLeft,
      right: arrowIndicatorRight,
    },
    mobileImage: claimsMobilePhone,
    phoneTwoConfig: phoneTwoConfigFr,
    lerpValue: {
      phoneOne: 30,
      phoneTwo: -30,
    },
  },
  bottomCTA: {
    title: 'A partir de 4 € /mois.',
    subtitle: 'Protégez vos affaires en quelques clics.',
    ctaUrl: '/onboarding/start?f=1&ownership=renter',
    ctaLabel: 'consultez nos prix',
    animationConfig,
  },
  products: {
    darkBackground: true,
    title: 'Plus de produits\nhabitation chez Lemonade',
    trackingLabel: 'renters',
    products: [
      {
        title: 'Propriétaires',
        subtitle: 'Protégez vos biens, ainsi que<br/>votre propriété',
        price: 'À PARTIR DE 10 € /MOIS',
        product: 'home_eu_owners',
        ctaLabelDesktop: 'consultez nos prix',
        ctaLabelMobile: 'prix',
        customUrl: '/onboarding/start?f=1&ownership=renter',
        alt: 'assurance habitation propriétaire',
      },
      {
        title: 'PNO',
        subtitle: 'Protégez votre logement en tant que propriétaire non-occupant',
        price: 'À PARTIR DE 6 € /MOIS',
        product: 'landlords_eu',
        ctaLabelDesktop: 'consultez nos prix',
        ctaLabelMobile: 'prix',
        customUrl: '/onboarding/start?f=1&ownership=renter',
        alt: 'assurance PNO',
      },
    ],
  },
};
